// Dependencies
import { memo, useEffect, useState, createRef, Dispatch, MouseEvent } from 'react';
import { Handle, Position, Node, NodeProps } from '@xyflow/react';
import { Edit, UnfoldLess, UnfoldMore, MoreVert } from '@material-ui/icons';

import { delays } from '../../../../utils/sequence';
import Note from '../../../Note';
import { convertSequenceToData } from '../../../../utils/sequence';
import { UbiverseEventTopicList, UbiverseNote } from '../../../../types';
import './index.scss';
import { IGlobalState, GlobalAction, GlobalActionTypeList } from '../../../../states/GlobalState';
import CONST from '../../../../consts';
import TimelineNote from '../../../TimelineNote';
import TimeIndicator from '../../../TimeIndicator';
import { getSecondsPerBeat } from '../../../../states/utils';
import { RealtimeClient } from '../../../../services/realtime-client';

const baseColumnCount = 100

export type TimelineViewerNodeData = {
  id: string;
  label: string;
  onClick: (topic: string, payload: any) => void;
  toggleEditMode: () => void;
  state: IGlobalState;
  dispatch: Dispatch<GlobalAction>;
  realtimeClient: RealtimeClient;
};
type TimelineViewerNodeProp = Node<TimelineViewerNodeData>;

export default memo(({ data, isConnectable }: NodeProps<TimelineViewerNodeProp>) => {
  const { state, dispatch, id, realtimeClient } = data
  const [cellSize, setCellSize] = useState<{ width: number, height: number }>({ width: 20, height: 20 });
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);

  const pianoRollRef = createRef<HTMLDivElement>();
  const timelineRef = createRef<HTMLDivElement>();
  const sequence = state.sequences.find(s => s.id === id)
  const sequenceNotes = sequence?.notes || []
  const isMuted = sequence?.isMuted ?? false
  const maxNotePosition = sequenceNotes.reduce((max, note) => Math.max(max, note.position), 0)
  const playheadPosition = state.control.playheadTime / getSecondsPerBeat(state.control.bpm, state.control.signature)
  const columnCount = Math.max(
    baseColumnCount,
    Math.ceil(maxNotePosition + 1),
    Math.ceil(playheadPosition + 1) + state.control.signature.numerator
  )

  useEffect(() => {
    if (!pianoRollRef.current) return
    const width = pianoRollRef.current.children[0].scrollWidth + 1
    const height = pianoRollRef.current.children[0].scrollHeight
    if (cellSize.width !== width || cellSize.height !== height) {
      setCellSize({ width, height })
    }
  }, [pianoRollRef, columnCount, cellSize.height, cellSize.width]);

  useEffect(() => {
    if (!timelineRef.current) return
    const scrollLeft = (state.control.playheadTime / getSecondsPerBeat(state.control.bpm, state.control.signature)) * cellSize.width
    timelineRef.current.scrollLeft = scrollLeft
  }, [state.control.playheadTime])

  const handleMuteToggle = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (state.control.isPlaying || !sequence) return
    const nextIsMuted = !isMuted
    dispatch({ type: GlobalActionTypeList.UpdateSequenceById, payload: { ...sequence, isMuted: nextIsMuted } })
    realtimeClient.emitEvent(UbiverseEventTopicList.SetMute, { isMute: nextIsMuted, nodeId: id })
  }

  return (
    <div className="node--timeline-viewer" onClick={() => { setIsDrawerOpen(false) }}>
      {/* Header Container */}
      <div className="node--timeline-viewer__header">
        <h5 className="node--timeline-viewer__header__text">{sequence?.name || data.label || 'Untitled timeline'}</h5>

        {/* CTA Container */}
        <div className="node--timeline-viewer__cta">
          <div
            className={`node--timeline-viewer__cta__button ${state.editor.targetId === id ? 'active' : ''}`}
            onClick={() => {
              dispatch({ type: GlobalActionTypeList.SetEditorTargetId, payload: id })
            }}
          >
            <Edit />
          </div>
          <div
            className={`node--timeline-viewer__cta__button ${isMuted ? 'active' : ''} ${state.control.isPlaying ? 'disabled' : ''}`}
            onClick={handleMuteToggle}
          >
            <h6 className="node--timeline-viewer__cta__button__text">M</h6>
          </div>
          <div className="node--timeline-viewer__cta__button">
            <h6 className="node--timeline-viewer__cta__button__text">S</h6>
          </div>
          <div
            className="node--timeline-viewer__cta__button"
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(prev => !prev);
            }}
          >
            {isMinimized ? <UnfoldMore /> : <UnfoldLess />}
          </div>
          <div className="node--timeline-viewer__cta__button" onClick={(e) => { e.stopPropagation(); setIsDrawerOpen(!isDrawerOpen) }}>
            <MoreVert />
          </div>
          {isDrawerOpen && (
            <div className="node--timeline-viewer__drawer" onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: GlobalActionTypeList.RemoveSequence, payload: id })
            }}>
              <p className="node--timeline-viewer__drawer__label">Delete this sequence</p>
            </div>
          )}
        </div>
      </div>

      {/* graph container */}
      <div className={`node--timeline-viewer__container ${isMinimized ? 'minimized' : ''}`} ref={timelineRef}>
        <div className="node--timeline-viewer__piano-roll"
          ref={pianoRollRef}
          style={{
            gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
            gridTemplateRows: `repeat(${CONST.sampleDelay.numberOfIntensities}, 1fr)`
          }}
        >
            {Array.from({ length: columnCount * CONST.sampleDelay.numberOfIntensities }).map((_, index) => (
              <div
                key={index}
                className={`node--timeline-viewer__piano-roll__cell ${index < columnCount ? 'top-border': ''} ${state.control.repeat?.isActive && index < columnCount && (index >= (state.control.repeat.from.bar - 1) * state.control.signature.numerator + state.control.repeat.from.beat - 1 && index < (state.control.repeat.to.bar - 1) * state.control.signature.numerator + state.control.repeat.to.beat - 1) ? 'in-repeat': ''} ${Math.floor(index / columnCount) === CONST.sampleDelay.numberOfIntensities / 2 ? 'middle-border': ''} ${Math.floor(index / columnCount) === CONST.sampleDelay.numberOfIntensities - 1 ? 'bottom-border': ''} ${(index % columnCount) % state.control.signature.numerator === 0 ? 'major-border': ''}`}
                style={{ minWidth: `20px` }}
              >
                {((index % columnCount) % state.control.signature.numerator === 0 && index < columnCount) &&
                  <h6 className={`node--timeline-viewer__piano-roll__cell__major-border__label`}>{(index /  state.control.signature.numerator) + 1}</h6>
                }
              </div>
            ))}
            <TimeIndicator state={state} cellSize={cellSize} isSmall={true} />
            <div className="node--timeline-viewer__piano-roll__note-container">
              {sequenceNotes.sort((a, b) => a.position - b.position).map((sequence, index) => {
                return (
                <TimelineNote
                  key={`${sequence.id}-${sequence.position}`}
                  small={true}
                  onMouseDown={(e) => {}}
                  state={state}
                  id={sequence.id}
                  position={sequence.position}
                  intensity={sequence.intensity}
                  cellSize={cellSize}
                  sequence={sequenceNotes}
                />
              )})}
            </div>
          </div>
      </div>

      {/* Handle */}
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={isConnectable}
      />
    </div>
  );
});
